import SectionContainer from 'stories/V1/layouts/SectionContainer'
import styled from 'styled-components'
import TeamPicture1 from 'assets/careers/team/team-picture-1.webp'
import TeamPicture2 from 'assets/careers/team/team-picture-2.webp'
import TeamPicture3 from 'assets/careers/team/team-picture-3.webp'
import TeamPicture4 from 'assets/careers/team/team-picture-4.webp'
import TeamPicture5 from 'assets/careers/team/team-picture-5.webp'
import TeamPicture6 from 'assets/careers/team/team-picture-6.webp'
import TeamPicture7 from 'assets/careers/team/team-picture-7.webp'
import TeamPicture8 from 'assets/careers/team/team-picture-8.webp'
import TeamPicture9 from 'assets/careers/team/team-picture-9.webp'
import TeamPicture10 from 'assets/careers/team/team-picture-10.webp'
import TeamPicture11 from 'assets/careers/team/team-picture-11.webp'
import TeamPicture12 from 'assets/careers/team/team-picture-12.webp'
import LeftArrow from 'assets/careers/team/left-arrow.svg'
import RightArrow from 'assets/careers/team/right-arrow.svg'
import { useState, useRef, useEffect, useMemo } from 'react'
import NextImage from 'components/elements/NextImage'

const CarouselContainer = styled.div`
    position: relative;
    width: 100%;
    padding: 15px 0;
    @media (max-width: 860px) {
        padding: 0px;
    }
`

const CarouselInner = styled.div`
    display: flex;
    align-items: center;
    max-width: 1068px;
    margin: 0 auto;
    padding: 0 24px;
    position: relative;
    @media (max-width: 860px) {
        padding: 0 12px;
    }
`

const TeamPicturesWrapper = styled.div`
    width: 100%;
    border-radius: 16px !important;
    overflow: hidden;
    position: relative;
`

const TeamPicturesGrid = styled.div`
    display: flex;
    gap: 8px;
    transition: transform 0.5s ease;

    @media (max-width: 860px) {
        overflow-x: auto;
        scrollbar-width: none;
        -ms-overflow-style: none;
        &::-webkit-scrollbar {
            display: none;
        }
        padding: 0px;
        transform: none !important;
    }
`

const TeamPicture = styled.div`
    flex: 0 0 384px;
    height: 256px;
    border-radius: 16px;
    overflow: hidden;
    position: relative;

    @media (max-width: 860px) {
        flex: 0 0 308px;
        height: 204px;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`

const ArrowButton = styled.div<{ direction: 'left' | 'right' }>`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    ${(props) => (props.direction === 'left' ? 'left: -24px;' : 'right: -24px;')}
    cursor: pointer;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 860px) {
        display: none;
    }
`

const CareersTeamPictures = () => {
    const originalPictures = useMemo(
        () => [
            TeamPicture1,
            TeamPicture2,
            TeamPicture3,
            TeamPicture4,
            TeamPicture5,
            TeamPicture6,
            TeamPicture7,
            TeamPicture8,
            TeamPicture9,
            TeamPicture10,
            TeamPicture11,
            TeamPicture12,
        ],
        []
    )

    const teamPictures = useMemo(() => {
        const clonedBefore = [...originalPictures.slice(-2)]
        const clonedAfter = [...originalPictures.slice(0, 2)]
        return [...clonedBefore, ...originalPictures, ...clonedAfter]
    }, [originalPictures])

    const [currentIndex, setCurrentIndex] = useState(2)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [imagesPreloaded, setImagesPreloaded] = useState(false)
    const carouselRef = useRef<HTMLDivElement>(null)
    const indexRef = useRef(currentIndex)

    const IMAGE_WIDTH = 384
    const GAP_WIDTH = 8
    const TOTAL_REAL_IMAGES = originalPictures.length

    useEffect(() => {
        const preloadImages = async () => {
            const imagePromises = teamPictures.map((img) => {
                return new Promise((resolve) => {
                    const image = new Image()
                    image.src = img.src
                    image.onload = resolve
                    image.onerror = resolve
                })
            })

            await Promise.all(imagePromises)
            setImagesPreloaded(true)
        }

        preloadImages()
    }, [teamPictures])

    useEffect(() => {
        indexRef.current = currentIndex
    }, [currentIndex])

    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 860
            setIsMobile(mobile)

            if (carouselRef.current && imagesPreloaded) {
                if (!mobile) {
                    scrollToIndex(currentIndex, false)
                }
            }
        }

        checkMobile()
        window.addEventListener('resize', checkMobile)

        return () => {
            window.removeEventListener('resize', checkMobile)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imagesPreloaded])

    useEffect(() => {
        if (carouselRef.current && !isMobile && imagesPreloaded) {
            scrollToIndex(currentIndex, false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imagesPreloaded])

    const scrollToIndex = (index: number, withTransition = true) => {
        if (!carouselRef.current || isMobile) return

        if (!withTransition) {
            carouselRef.current.style.transition = 'none'
        } else {
            carouselRef.current.style.transition = 'transform 0.5s ease'
        }

        const offset = index * (IMAGE_WIDTH + GAP_WIDTH)
        carouselRef.current.style.transform = `translateX(-${offset}px)`

        if (!withTransition) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            carouselRef.current.offsetHeight
            carouselRef.current.style.transition = 'transform 0.5s ease'
        }
    }

    useEffect(() => {
        if (isTransitioning && !isMobile) {
            const timer = setTimeout(() => {
                if (currentIndex >= TOTAL_REAL_IMAGES + 2) {
                    setCurrentIndex(2)
                    scrollToIndex(2, false)
                } else if (currentIndex < 2) {
                    setCurrentIndex(TOTAL_REAL_IMAGES + 1)
                    scrollToIndex(TOTAL_REAL_IMAGES + 1, false)
                }

                setIsTransitioning(false)
            }, 500)

            return () => clearTimeout(timer)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTransitioning, currentIndex, TOTAL_REAL_IMAGES, isMobile])

    const handleNext = () => {
        if (isTransitioning || !imagesPreloaded) return
        const newIndex = currentIndex + 2
        setCurrentIndex(newIndex)
        setIsTransitioning(true)
        scrollToIndex(newIndex)
    }

    const handlePrev = () => {
        if (isTransitioning || !imagesPreloaded) return
        const newIndex = currentIndex - 2
        setCurrentIndex(newIndex)
        setIsTransitioning(true)
        scrollToIndex(newIndex)
    }

    return (
        <SectionContainer backgroundColor={'#0b0b0d'} title="">
            <CarouselContainer>
                <CarouselInner>
                    <ArrowButton direction="left" onClick={handlePrev}>
                        <LeftArrow />
                    </ArrowButton>

                    <TeamPicturesWrapper>
                        <TeamPicturesGrid ref={carouselRef}>
                            {isMobile
                                ? originalPictures.map((picture, index) => (
                                      <TeamPicture key={`mobile-${index}`}>
                                          <NextImage
                                              src={picture.src}
                                              alt={`team photo ${index + 1}`}
                                              quality={100}
                                              width={308}
                                              height={204}
                                              priority={index < 2}
                                          />
                                      </TeamPicture>
                                  ))
                                : teamPictures.map((picture, index) => (
                                      <TeamPicture key={`desktop-${index}`}>
                                          <NextImage
                                              src={picture.src}
                                              alt={`team photo ${(index % TOTAL_REAL_IMAGES) + 1}`}
                                              quality={100}
                                              width={384}
                                              height={256}
                                              priority={true}
                                          />
                                      </TeamPicture>
                                  ))}
                        </TeamPicturesGrid>
                    </TeamPicturesWrapper>

                    <ArrowButton direction="right" onClick={handleNext}>
                        <RightArrow />
                    </ArrowButton>
                </CarouselInner>
            </CarouselContainer>
        </SectionContainer>
    )
}

export default CareersTeamPictures
